import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

/* ── 타입 (BE StoreFeed* DTO 와 정합) ── */
export interface StoreFeedPhoto {
  fileSeq: number;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  thumbnailStatus: string | null;
}

export interface StoreFeed {
  feedSeq: number;
  companySeq: number;
  companyName: string | null;
  companyFileUrl: string | null;
  authorName: string | null;
  content: string | null;
  dtEvent: string | null;
  viewCount: number;
  editedYn: boolean;
  dtRegist: string;
  photos: StoreFeedPhoto[];
}

export interface StoreFeedCreateRequest {
  content: string | null;
  dtEvent: string | null;
  imageFileSeqs: number[];
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  number: number;
  size: number;
}

export const STORE_FEED_QUERY_KEYS = {
  myFeed: ["business", "feed", "my"] as const,
};

const FEED = API_ENDPOINTS.BUSINESS_FEED;

/* ── API ── */
const createStoreFeed = async (req: StoreFeedCreateRequest) => {
  const { data } = await privateHttp.post(FEED.CREATE, req);
  return data;
};

const fetchMyStoreFeed = async (page: number, size: number) => {
  const { data } = await privateHttp.get(FEED.LIST, { params: { page, size } });
  return data.data as PageResponse<StoreFeed>;
};

const updateStoreFeed = async (
  feedSeq: number,
  req: StoreFeedCreateRequest,
) => {
  const { data } = await privateHttp.put(FEED.UPDATE(feedSeq), req);
  return data;
};

const deleteStoreFeed = async (feedSeq: number) => {
  const { data } = await privateHttp.delete(FEED.DELETE(feedSeq));
  return data;
};

/* ── Hooks ── */
export const useCreateStoreFeed = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createStoreFeed,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: STORE_FEED_QUERY_KEYS.myFeed }),
  });
};

export const useMyStoreFeed = (size = 20) => {
  return useInfiniteQuery({
    queryKey: [...STORE_FEED_QUERY_KEYS.myFeed, size],
    queryFn: ({ pageParam = 0 }) => fetchMyStoreFeed(pageParam as number, size),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + (p.content?.length ?? 0), 0);
      if (loaded === 0 || loaded >= (lastPage.totalElements ?? 0))
        return undefined;
      return allPages.length;
    },
  });
};

export const useUpdateStoreFeed = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      feedSeq,
      req,
    }: {
      feedSeq: number;
      req: StoreFeedCreateRequest;
    }) => updateStoreFeed(feedSeq, req),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: STORE_FEED_QUERY_KEYS.myFeed }),
  });
};

export const useDeleteStoreFeed = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedSeq: number) => deleteStoreFeed(feedSeq),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: STORE_FEED_QUERY_KEYS.myFeed }),
  });
};
